import { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, QrCode, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ISSUE_LABEL = {
  not_configured: "Sin enlace configurado",
  private_inactive: "Consultorio privado no activado",
  inactive: "Médico no activo",
};

const downloadQR = (container, filename) => {
  const svg = container?.querySelector("svg");
  if (!svg) return toast.error("No se pudo generar el QR");
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  const img = new Image();
  img.onload = () => {
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, 512, 512);
    ctx.drawImage(img, 0, 0, 512, 512);
    const a = document.createElement("a");
    a.download = filename;
    a.href = canvas.toDataURL("image/png");
    a.click();
    toast.success("QR descargado");
  };
  img.onerror = () => toast.error("No se pudo generar el QR");
  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(svg))));
};

const QRDialog = ({ qr, onClose }) => {
  const ref = useRef(null);
  return (
    <Dialog open={!!qr} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="admin-link-qr-dialog">
        <DialogHeader>
          <DialogTitle>{qr?.doctorName}</DialogTitle>
          <DialogDescription>{qr?.institutionName}</DialogDescription>
        </DialogHeader>
        <div ref={ref} className="flex justify-center p-4 bg-white rounded-lg border">
          {qr && <QRCodeSVG value={qr.url} size={220} level="M" includeMargin />}
        </div>
        <p className="text-xs text-slate-500 break-all text-center">{qr?.url}</p>
        <Button onClick={() => downloadQR(ref.current, qr.filename)} data-testid="admin-link-qr-download-btn">
          <Download className="h-4 w-4 mr-2" /> Descargar QR
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const AdminDoctorLinks = ({ doctor }) => {
  const [qr, setQr] = useState(null);
  const links = doctor.smart_links || [];
  if (links.length === 0) return <span className="text-xs text-slate-400">Sin enlace configurado</span>;

  const copy = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Enlace de ${label} copiado`);
    } catch {
      toast.error("Error al copiar");
    }
  };

  return (
    <div className="space-y-1.5 min-w-[220px]" data-testid={`admin-doctor-links-${doctor.id}`}>
      {links.map((l, i) => {
        const url = l.path ? `${window.location.origin}${l.path}` : null;
        return (
          <div key={i} className="flex items-center gap-1.5">
            <span className="text-xs text-slate-600 truncate max-w-[130px]" title={l.institution_name}>{l.institution_name}</span>
            {l.issue ? (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`admin-link-issue-${doctor.id}-${i}`}>
                {ISSUE_LABEL[l.issue]}
              </span>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" title={url} onClick={() => copy(url, l.institution_name)} data-testid={`admin-link-copy-${doctor.id}-${i}`}>
                  <Copy className="h-3 w-3 mr-1" /> Copiar
                </Button>
                <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => setQr({ url, doctorName: doctor.name, institutionName: l.institution_name, filename: `qr${l.path.replace(/\//g, "-")}.png` })} data-testid={`admin-link-qr-${doctor.id}-${i}`}>
                  <QrCode className="h-3 w-3 mr-1" /> QR
                </Button>
              </>
            )}
          </div>
        );
      })}
      <QRDialog qr={qr} onClose={() => setQr(null)} />
    </div>
  );
};
